import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthSession } from '../hooks/useAuthSession';
import { supabase } from '../supabase/client';
import EnhancedCSRAssignment from '../components/admin/EnhancedCSRAssignment';
import AdminQuotaManagement from '../components/admin/AdminQuotaManagement';
import AdminUserManagement from '../components/admin/AdminUserManagement';
import AdminPresetFilterManagement from '../components/admin/AdminPresetFilterManagement';
import AdminCompanyManagement from '../components/admin/AdminCompanyManagement';
import AdminPricingConfiguration from '../components/admin/AdminPricingConfiguration';
import OutreachTeamManagement from '../components/admin/OutreachTeamManagement';
import AdminDemoRequests from '../components/admin/AdminDemoRequests';
import AdminTemplateManagement from '../components/admin/AdminTemplateManagement';
import AdminAIPromptsManagement from '../components/admin/AdminAIPromptsManagement';
import AdminDripifyEntry from '../components/admin/AdminDripifyEntry';

export default function EnhancedAdminPanel() {
  const navigate = useNavigate();
  const { setSession } = useAuthSession();
  const [activeTab, setActiveTab] = useState<'users' | 'campaigns' | 'companies' | 'quotas' | 'filters' | 'pricing' | 'outreach-team' | 'demo-requests' | 'templates' | 'ai-prompts' | 'dripify'>('campaigns');

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow">
          {/* Header */}
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
                <p className="text-gray-600 text-sm">
                  Enhanced admin panel with user, campaign, and company management
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleLogout}
                  className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="flex px-6">
              <button
                onClick={() => setActiveTab('users')}
                className={`px-4 py-2 font-medium text-sm border-b-2 ${
                  activeTab === 'users'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                User Management
              </button>
              <button
                onClick={() => setActiveTab('campaigns')}
                className={`px-4 py-2 font-medium text-sm border-b-2 ${
                  activeTab === 'campaigns'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                CSR Assignment
              </button>
              <button
                onClick={() => setActiveTab('companies')}
                className={`px-4 py-2 font-medium text-sm border-b-2 ${
                  activeTab === 'companies'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Company Management
              </button>
              <button
                onClick={() => setActiveTab('quotas')}
                className={`px-4 py-2 font-medium text-sm border-b-2 ${
                  activeTab === 'quotas'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Quota Management
              </button>
              <button
                onClick={() => setActiveTab('filters')}
                className={`px-4 py-2 font-medium text-sm border-b-2 ${
                  activeTab === 'filters'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Preset Filters
              </button>
              <button
                onClick={() => setActiveTab('pricing')}
                className={`px-4 py-2 font-medium text-sm border-b-2 ${
                  activeTab === 'pricing'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Pricing Configuration
              </button>
              <button
                onClick={() => setActiveTab('outreach-team')}
                className={`px-4 py-2 font-medium text-sm border-b-2 ${
                  activeTab === 'outreach-team'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Outreach Team
              </button>
              <button
                onClick={() => setActiveTab('templates')}
                className={`px-4 py-2 font-medium text-sm border-b-2 ${
                  activeTab === 'templates'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Templates
              </button>
              <button
                onClick={() => setActiveTab('demo-requests')}
                className={`px-4 py-2 font-medium text-sm border-b-2 ${
                  activeTab === 'demo-requests'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Demo Requests
              </button>
              <button
                onClick={() => setActiveTab('ai-prompts')}
                className={`px-4 py-2 font-medium text-sm border-b-2 ${
                  activeTab === 'ai-prompts'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                AI Prompts
              </button>
              <button
                onClick={() => setActiveTab('dripify')}
                className={`px-4 py-2 font-medium text-sm border-b-2 ${
                  activeTab === 'dripify'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Dripify
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'users' ? (
            <AdminUserManagement />
          ) : activeTab === 'campaigns' ? (
            <EnhancedCSRAssignment />
          ) : activeTab === 'companies' ? (
            <AdminCompanyManagement />
          ) : activeTab === 'quotas' ? (
            <AdminQuotaManagement />
          ) : activeTab === 'pricing' ? (
            <AdminPricingConfiguration />
          ) : activeTab === 'outreach-team' ? (
            <OutreachTeamManagement />
          ) : activeTab === 'templates' ? (
            <AdminTemplateManagement />
          ) : activeTab === 'demo-requests' ? (
            <AdminDemoRequests />
          ) : activeTab === 'ai-prompts' ? (
            <AdminAIPromptsManagement />
          ) : activeTab === 'dripify' ? (
            <AdminDripifyEntry />
          ) : (
            <AdminPresetFilterManagement />
          )}
        </div>
      </div>
    </div>
  );
}